import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  AlertTriangle, CheckCircle, Search, ChevronDown, ChevronUp,
  DollarSign, User, Calendar, RefreshCw, Link, X,
} from 'lucide-react';

const TODAY = new Date().toLocaleDateString('en-CA');

const getMonthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
};

// preset ids
const PRESET_TODAY  = 'today';
const PRESET_MONTH  = 'month';
const PRESET_CUSTOM = 'custom';

const METHOD_LABELS = { cash: 'CASH', jazzcash: 'JazzCash', easypaisa: 'EasyPaisa', bank: 'Bank' };

export default function MissingEntries() {
  const navigate = useNavigate();

  const [preset, setPreset]     = useState(PRESET_TODAY);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState({});
  const [search, setSearch]     = useState('');

  // Link-customer modal
  const [linkModal, setLinkModal]           = useState(null); // { activationId, currentUsername }
  const [linkSearch, setLinkSearch]         = useState('');
  const [linkResults, setLinkResults]       = useState([]);
  const [linkSearching, setLinkSearching]   = useState(false);
  const [linking, setLinking]               = useState(false);
  const [linkError, setLinkError]           = useState('');
  const linkSearchTimeout                   = useRef(null);

  const openLinkModal = (entry) => {
    setLinkModal({ activationId: entry.activation_id, currentUsername: entry.username });
    setLinkSearch(entry.username);
    setLinkResults([]);
    setLinkError('');
    searchCustomers(entry.username);
  };

  const closeLinkModal = () => {
    setLinkModal(null);
    setLinkSearch('');
    setLinkResults([]);
    setLinkError('');
  };

  const searchCustomers = (query) => {
    clearTimeout(linkSearchTimeout.current);
    if (!query || query.length < 2) { setLinkResults([]); return; }
    linkSearchTimeout.current = setTimeout(async () => {
      setLinkSearching(true);
      try {
        const res = await api.get('/customers/', { params: { search: query, page_size: 10 } });
        setLinkResults(res.data.results || res.data || []);
      } catch {
        setLinkResults([]);
      } finally {
        setLinkSearching(false);
      }
    }, 300);
  };

  const confirmLink = async (customer) => {
    if (!linkModal) return;
    setLinking(true);
    setLinkError('');
    try {
      await api.patch(`/activations/${linkModal.activationId}/link-customer/`, {
        customer_id: customer.id,
      });
      closeLinkModal();
      fetchData(); // refresh the missing entries list
    } catch (err) {
      setLinkError(err.response?.data?.error || 'Failed to link customer.');
    } finally {
      setLinking(false);
    }
  };

  const buildParams = useCallback(() => {
    if (preset === PRESET_TODAY)  return { date: TODAY };
    if (preset === PRESET_MONTH)  return { date_from: getMonthStart(), date_to: TODAY };
    // Custom
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (!dateFrom && !dateTo) p.date = TODAY;
    return p;
  }, [preset, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/activations/missing-collections/', { params: buildParams() });
      setData(res.data);
      // Auto-expand all officers that have missing entries
      const init = {};
      res.data.by_officer.forEach(o => { init[o.officer_id ?? 0] = true; });
      setExpanded(init);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load missing entries.');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Auto-fetch when Today or This Month is selected; Custom waits for Apply
  useEffect(() => {
    if (preset !== PRESET_CUSTOM) fetchData();
  }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load
  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOfficer = (id) =>
    setExpanded(prev => ({ ...prev, [id ?? 0]: !prev[id ?? 0] }));

  const isAdmin = data?.viewer_is_admin ?? true;

  const filteredOfficers = data?.by_officer.map(officer => ({
    ...officer,
    entries: search
      ? officer.entries.filter(e =>
          e.username.toLowerCase().includes(search.toLowerCase()) ||
          e.name.toLowerCase().includes(search.toLowerCase())
        )
      : officer.entries,
  })).filter(o => o.entries.length > 0) ?? [];

  const totalMissing = data?.summary.total_missing ?? 0;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={24} />
            {isAdmin ? 'Missing Collection Entries' : 'My Missing Collections'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isAdmin
              ? 'Customers in the activation log with no officer collection entry in the same period'
              : 'Customers activated by the clerk that you have not yet collected for'}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {[
            { id: PRESET_TODAY, label: 'Today' },
            { id: PRESET_MONTH, label: 'This Month' },
            { id: PRESET_CUSTOM, label: 'Custom Range' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setPreset(id)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                preset === id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {preset === PRESET_CUSTOM && (
          <div className="flex flex-wrap gap-3 pt-1">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchData}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Active filter description */}
        <p className="text-xs text-gray-400">
          {preset === PRESET_TODAY  && `Activations logged today — collections checked for today`}
          {preset === PRESET_MONTH  && `Activations logged this month — collections checked this month`}
          {preset === PRESET_CUSTOM && dateFrom && `Activations ${dateFrom}${dateTo ? ` → ${dateTo}` : ''} — collections checked in the same window`}
        </p>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
            <p className="text-xs font-bold text-gray-400 uppercase mb-1">Activations</p>
            <p className="text-3xl font-bold text-gray-800">{data.summary.total_activations}</p>
          </div>
          <div className="bg-white rounded-xl border border-green-100 shadow-sm p-4 text-center">
            <p className="text-xs font-bold text-green-500 uppercase mb-1">Collected</p>
            <p className="text-3xl font-bold text-green-600">{data.summary.total_collected}</p>
          </div>
          <div className={`rounded-xl border shadow-sm p-4 text-center ${
            totalMissing > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'
          }`}>
            <p className={`text-xs font-bold uppercase mb-1 ${totalMissing > 0 ? 'text-red-500' : 'text-gray-400'}`}>
              Missing
            </p>
            <p className={`text-3xl font-bold ${totalMissing > 0 ? 'text-red-600' : 'text-gray-800'}`}>
              {totalMissing}
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw size={24} className="animate-spin mr-3" />
          Loading…
        </div>
      )}

      {/* All clear */}
      {!loading && data && totalMissing === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-green-600 gap-3">
          <CheckCircle size={48} />
          <p className="text-lg font-bold">All activations have collection entries</p>
          <p className="text-sm text-gray-500">
            {data.summary.total_activations} activation{data.summary.total_activations !== 1 ? 's' : ''} — all collected
          </p>
        </div>
      )}

      {/* Search */}
      {!loading && totalMissing > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by username or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      )}

      {/* Officer groups */}
      {!loading && filteredOfficers.map(officer => {
        const key = officer.is_unlinked_group ? '__unlinked__' : (officer.officer_id ?? 0);
        const isOpen = expanded[key] !== false;
        const isUnlinked = officer.is_unlinked_group;
        // Officers see a flat table — no collapsible officer header needed
        const showHeader = isAdmin || isUnlinked;

        return (
          <div key={key} className={`rounded-xl border shadow-sm overflow-hidden ${
            isUnlinked ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'
          }`}>
            {/* Officer header — admins only (or unlinked group) */}
            {showHeader && <button
              onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
              className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${
                isUnlinked ? 'hover:bg-amber-100' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  isUnlinked ? 'bg-amber-200' : 'bg-blue-100'
                }`}>
                  {isUnlinked
                    ? <AlertTriangle size={18} className="text-amber-700" />
                    : <User size={18} className="text-blue-600" />
                  }
                </div>
                <div className="text-left">
                  <p className={`font-bold ${isUnlinked ? 'text-amber-800' : 'text-gray-800'}`}>
                    {officer.officer_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    PKR {officer.missing_amount.toLocaleString()} · {isUnlinked ? 'link usernames to customer records to resolve' : 'not yet collected'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 text-sm font-bold rounded-full ${
                  isUnlinked ? 'bg-amber-200 text-amber-800' : 'bg-red-100 text-red-700'
                }`}>
                  {officer.missing_count} {isUnlinked ? 'unlinked' : 'missing'}
                </span>
                {isOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </div>
            </button>}

            {/* Entries table — always open for officers; collapsible for admins */}
            {(isOpen || !showHeader) && (
              <div className="border-t border-gray-100 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">Username</th>
                      <th className="px-4 py-2 text-left">Area</th>
                      <th className="px-4 py-2 text-left">Package</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-left">Method</th>
                      <th className="px-4 py-2 text-left">Act. Date</th>
                      <th className="px-4 py-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {officer.entries.map((entry, idx) => (
                      <tr key={entry.customer_id ?? `unlinked-${idx}`}
                          className={`transition-colors ${isUnlinked ? 'hover:bg-amber-100' : 'hover:bg-amber-50'}`}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-800">{entry.username}</p>
                          {!isUnlinked && <p className="text-xs text-gray-400">{entry.name}</p>}
                          {isUnlinked && <p className="text-xs text-amber-600">Not linked</p>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{entry.area}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{entry.package}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">
                          PKR {entry.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600">
                            {METHOD_LABELS[entry.payment_method] || entry.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            <Calendar size={11} />
                            {entry.activation_date}
                          </span>
                        </td>
                        {!isUnlinked ? (
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => navigate(`/collections/new?customer=${entry.customer_id}`)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 whitespace-nowrap"
                            >
                              <DollarSign size={11} />
                              Collect
                            </button>
                          </td>
                        ) : (
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => openLinkModal(entry)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 whitespace-nowrap"
                            >
                              <Link size={11} />
                              Link Customer
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      {/* Link Customer Modal */}
      {linkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Link size={16} className="text-amber-600" />
                  Link Customer
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Activation logged as <strong>{linkModal.currentUsername}</strong>
                </p>
              </div>
              <button onClick={closeLinkModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search by username or name…"
                  value={linkSearch}
                  onChange={e => { setLinkSearch(e.target.value); searchCustomers(e.target.value); }}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              {linkError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {linkError}
                </p>
              )}

              <div className="max-h-64 overflow-y-auto divide-y divide-gray-50 rounded-lg border border-gray-100">
                {linkSearching && (
                  <div className="flex items-center justify-center py-6 text-gray-400 text-sm gap-2">
                    <RefreshCw size={14} className="animate-spin" /> Searching…
                  </div>
                )}
                {!linkSearching && linkResults.length === 0 && linkSearch.length >= 2 && (
                  <p className="text-center py-6 text-sm text-gray-400">No customers found</p>
                )}
                {linkResults.map(c => (
                  <button
                    key={c.id}
                    disabled={linking}
                    onClick={() => confirmLink(c)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-50 transition-colors text-left disabled:opacity-50"
                  >
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{c.username}</p>
                      <p className="text-xs text-gray-400">{c.name} · {c.area_name}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      c.status === 'active' ? 'bg-green-100 text-green-700' :
                      c.status === 'expired' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {c.status}
                    </span>
                  </button>
                ))}
              </div>

              {linking && (
                <p className="text-center text-sm text-amber-600 font-semibold animate-pulse">
                  Linking…
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
